/**
 * This file is part of the "Awaken Media" project.
 *
 * (c) 2020 - CanalPlus International
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Props, FormattedOperator } from '../types';
import {
  Wrapper,
  Title,
  Subtitle,
  Informations,
  InformationsIcon,
  Methods,
  InformationsContainer
} from '../styles/components';
import PaymentMethod from './PaymentMethod';
import CurrencyChoiceModal from './CurrencyChoiceModal';

export default function SubscriptionPaymentMethod({
  title,
  subtitle,
  iconUrl,
  iconAlt,
  moreInformation,
  paymentMethods,
  currencies,
  persistCurrency,
  redirect: redirectToPaymentMethod
}: Props) {
  const { t } = useTranslation();

  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [paymentMethodRedirectLink, setPaymentMethodRedirectLink] = useState<
    string | undefined
  >();

  const handleCloseCurrencyModal = useCallback(() => {
    setIsCurrencyModalOpen(false);
  }, []);

  const handleValidateCurrencyModal = useCallback(
    (currencies: { code: string; label: string }[]) => {
      // currency selection is in "unique" mode (radio button), so we pick the only item in list
      const currency = currencies[0];
      persistCurrency(currency.code);
      if (paymentMethodRedirectLink) {
        redirectToPaymentMethod(paymentMethodRedirectLink);
      }
    },
    [persistCurrency, paymentMethodRedirectLink, redirectToPaymentMethod]
  );

  const handleOperatorClick = useCallback(
    (operator: FormattedOperator) => {
      if (currencies.length <= 1) {
        if (currencies.length === 1) {
          persistCurrency(currencies[0]);
        }

        redirectToPaymentMethod(operator.link);
      } else {
        setPaymentMethodRedirectLink(operator.link);
        setIsCurrencyModalOpen(true);
      }
    },
    [
      currencies,
      persistCurrency,
      paymentMethodRedirectLink,
      redirectToPaymentMethod
    ]
  );

  return (
    <>
      {isCurrencyModalOpen && (
        <CurrencyChoiceModal
          currencies={currencies}
          onCloseClick={handleCloseCurrencyModal}
          onValidateClick={handleValidateCurrencyModal}
        />
      )}

      <Wrapper data-testid="subscription-payment-method">
        <Title>
          {title ?? t('blocks.selfcare.subscriptionPaymentMethods.title')}
        </Title>

        <InformationsContainer>
          {iconUrl ? (
            <InformationsIcon
              src={iconUrl}
              width="24px"
              height="24px"
              alt={iconAlt}
            />
          ) : null}

          <Informations>
            {moreInformation ??
              t('blocks.selfcare.subscriptionPaymentMethods.informations')}
          </Informations>
        </InformationsContainer>

        <Subtitle>
          {subtitle ?? t('blocks.selfcare.subscriptionPaymentMethods.subtitle')}
        </Subtitle>

        <Methods>
          {paymentMethods.map(method => (
            <PaymentMethod
              onNonMobileOperatorClick={handleOperatorClick}
              method={method}
              key={method.title}
            />
          ))}
        </Methods>
      </Wrapper>
    </>
  );
}
